import {
  StrictMode,
  ReactNode,
  Suspense,
  ReactElement,
  useEffect,
} from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "styled-components";
import { useAtomValue } from "jotai";
import { isAxiosError, isCancel } from "axios";

import { useServerPreload } from "_/data/server";
import { I18nProvider } from "_/i18n";
import { themeAtom } from "_/state";
import { GlobalStyle, getTheme } from "_/style";
import { startOtel, stopOtel } from "_/utils/otel";

import { LoadingOverlay } from "_/components/loading-overlay";
import { ErrorBoundary } from "_/components/error-boundary";
import { RequestInterceptor } from "_/components/request-interceptor";

import * as S from "./styled";

type WrappersProps = {
  children: ReactNode;
};

// The query client is essentially the cache and datastore used
// by react-query for caching returned data and managing
// query/request state.
//
// Direct access can be done via the `useQueryClient` hook.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        !isCancel(error) &&
        !(isAxiosError(error) && error.response?.status === 403) &&
        failureCount < 3,
    },
  },
});

// Starts or stops telemetry once the server's preload says where to send it.
const Telemetry = () => {
  const { data: preload, isError } = useServerPreload();

  useEffect(() => {
    if (preload) {
      startOtel({
        endpoint: preload.telemetryEndpoint,
        environment: preload.environment,
      });
    } else if (isError) {
      stopOtel();
    }
  }, [preload, isError]);

  return null;
};

export const Wrappers = ({ children }: WrappersProps): ReactElement => {
  const theme = useAtomValue(themeAtom);

  const notHeadless = !navigator.webdriver;
  const queryDevTools =
    __DEV__ && notHeadless ? (
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
    ) : null;

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RequestInterceptor>
          <Telemetry />
          <ThemeProvider theme={getTheme(theme)}>
            <GlobalStyle />
            <I18nProvider>
              <ErrorBoundary>
                <Suspense fallback={<LoadingOverlay />}>
                  <S.AppShell>{children}</S.AppShell>
                </Suspense>
              </ErrorBoundary>
            </I18nProvider>
          </ThemeProvider>
        </RequestInterceptor>
        {queryDevTools}
      </QueryClientProvider>
    </StrictMode>
  );
};
